/**
 * CSV export for the filtered view currently on screen (US-A12 AC4, US-A06).
 * Client-side on purpose: the rows are already fetched and the volumes are
 * small. If the ledger outgrows a browser export, this becomes an edge
 * function — nothing else changes.
 */
export type CsvColumn<T> = {
  header: string
  value: (row: T) => string | number | null | undefined
}

function escapeCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const text = String(value)
  // Guard against formula injection in Excel / Sheets.
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text
  return /[",\n;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map(column => escapeCell(column.header)).join(',')
  const body = rows.map(row => columns.map(column => escapeCell(column.value(row))).join(','))
  return [header, ...body].join('\n')
}

export function downloadCsv<T>(fileName: string, rows: T[], columns: CsvColumn<T>[]): void {
  // BOM so Excel opens UTF-8 Arabic correctly.
  const blob = new Blob([`\uFEFF${toCsv(rows, columns)}`], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(url)
}
